/**
 * Top 3 actions — the findings block on the control tower.
 *
 * Three kinds of candidate compete on ONE rupee scale, so the ranking is
 * meaningful rather than three separate lists stacked:
 *
 *  · availability, valued as unserved demand — the OSA gap to the portfolio
 *    average applied to the units actually sold, at the period's own realised
 *    value per unit;
 *  · the worst sub-line, weighted ×3 because a portfolio-wide problem should
 *    out-rank a single city;
 *  · supply, valued at the undelivered units on purchase orders that closed.
 *
 * It deliberately does NOT use the sales-loss estimate: that figure runs about
 * eight times the measured loss, so mixing it in would put every availability
 * item above every supply item for a reason that is an artefact of basis.
 *
 * Everything is recomputed for the selected window — change the dates and these
 * three change with them.
 */
import type { ReactNode } from 'react';
import { TableCard, fmt } from '../ds';
import { useReport } from '../useReport';
import { cityRowsOf } from './CitiesCard';
import { subLineRowsOf } from './SubLinesCard';
import { type Basis, type OpsWindow, Tag, num, periodLabel, str } from './opsLib';
import type { DateRange } from '@/lib/dateRange';

interface Candidate {
  /** Value at risk, in rupees — the one scale all three kinds sort on. */
  w: number;
  kind: 'City' | 'Sub-line' | 'Supply';
  who: string;
  text: ReactNode;
  /** The figures behind the claim, so it can be checked without leaving the
   *  page. Always the raw pair or the per-unit valuation, never a restatement
   *  of the sentence above it. */
  math: string;
}

export function ActionsCard({ customer, range, basis }: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  // The portfolio averages are pooled by the database over the whole scope.
  // Pooling the city percentages here instead would average a ratio, which is
  // the one thing this report never does.
  const head = useReport('/v1/reports/ops/control-tower', customer, range);
  const cities = useReport('/v1/reports/ops/cities', customer, range);
  const subs = useReport('/v1/reports/ops/sub-lines', customer, range);
  const po = useReport('/v1/reports/ops/fill-rate', customer, range);

  const w: OpsWindow | undefined = head.data?.window;
  const scope = periodLabel(w?.start, w?.end);
  const loading = head.isLoading || cities.isLoading || subs.isLoading || po.isLoading;

  const current = (head.data?.periods ?? []).find((p) => p['period'] === 'current');
  const pOsa = num(current?.['osa_unweighted']);
  const pOsaWeighted = num(current?.['osa_weighted']);

  const cityRows = cityRowsOf(cities.data?.rows ?? [], basis);
  const subRows = subLineRowsOf(subs.data?.rows ?? [], basis);

  /**
   * Value per unit for the period, on the selected basis.
   *
   * The realised value of what actually sold, not an assumption — which is why
   * the rupee scale of this card moves with the MRP/SP toggle. ₹80 is the
   * fallback the published page uses when there are no units to divide by.
   */
  const gmvTotal = cityRows.reduce((a, c) => a + (c.gmv ?? 0), 0);
  const unitTotal = cityRows.reduce((a, c) => a + (c.units ?? 0), 0);
  const vpu = unitTotal > 0 ? gmvTotal / unitTotal : 80;

  const cand: Candidate[] = [];

  // City availability: every city pooling below the portfolio's own unweighted
  // average. Both sides are the unweighted definition — comparing a city's
  // unweighted OSA against a sales-weighted portfolio figure would rank cities
  // by their product mix.
  if (pOsa !== null) {
    for (const c of cityRows) {
      if (c.osa === null || c.osa >= pOsa) continue;
      const gap = (pOsa - c.osa) / 100;
      const lost = (c.units ?? 0) * (gap / Math.max(c.osa / 100, 0.01));
      cand.push({
        w: lost * vpu,
        kind: 'City',
        who: c.name,
        text: (
          <>
            <b>{c.name} is running {c.osa.toFixed(1)}% OSA</b> against a {pOsa.toFixed(1)}%
            portfolio average — roughly {fmt(lost, 'int')} units of demand unserved.
          </>
        ),
        math: `OSA ${c.osa.toFixed(1)}% vs ${pOsa.toFixed(1)}% pooled, unweighted · ${fmt(c.units, 'int')} units sold · valued at ${fmt(vpu, 'money')}/unit`,
      });
    }
  }

  // The worst sub-line, only when it is more than three points below average,
  // and weighted x3 so a portfolio-wide problem out-ranks one city. Measured
  // against the SALES-WEIGHTED portfolio figure, because sub-line OSA is
  // sales-weighted and the two definitions are not interchangeable.
  const worst = subRows.filter((s) => s.osa !== null).sort((a, b) => (a.osa ?? 0) - (b.osa ?? 0))[0];
  if (worst?.osa != null && pOsaWeighted !== null && pOsaWeighted - worst.osa > 3) {
    cand.push({
      w: (worst.units ?? 0) * ((pOsaWeighted - worst.osa) / 100) * vpu * 3,
      kind: 'Sub-line',
      who: worst.name,
      text: (
        <>
          <b>{worst.name} has the worst availability in the portfolio at {worst.osa.toFixed(1)}%</b>,{' '}
          {(pOsaWeighted - worst.osa).toFixed(1)} points below the sales-weighted average.
        </>
      ),
      math: `sales-weighted OSA, pooled over the window · ${fmt(worst.units, 'int')} units · weighted ×3 as a portfolio-wide item`,
    });
  }

  // Supply: the three worst lanes by units short.
  const lanes = (po.data?.rows ?? []).map((r) => ({
    sku: str(r['sku']) || '—',
    fac: str(r['facility']) || '—',
    city: str(r['city']) || '—',
    fill: num(r['fill_rate']),
    lines: num(r['closed_lines']) ?? 0,
    ordered: num(r['closed_units_ordered']) ?? 0,
    delivered: num(r['closed_delivered_quantity']) ?? 0,
    short: num(r['closed_short']) ?? 0,
  })).filter((l) => l.ordered > 0 && l.short > 0)
    .sort((a, b) => b.short - a.short)
    .slice(0, 3);

  for (const l of lanes) {
    cand.push({
      w: l.short * vpu,
      kind: 'Supply',
      who: `${l.sku} · ${l.fac}`,
      text: (
        <>
          <b>{l.sku} into {l.fac} closed {fmt(l.short, 'int')} units short</b>
          {l.fill === null ? '' : ` at ${l.fill.toFixed(1)}% fill rate`}, across {l.lines} PO
          line{l.lines === 1 ? '' : 's'} in this window.
        </>
      ),
      math: `${l.city} · delivered ${fmt(l.delivered, 'int')} / ordered ${fmt(l.ordered, 'int')} · closed lines only, order-date basis`,
    });
  }

  const top = cand.sort((a, b) => b.w - a.w).slice(0, 3);

  if (loading) {
    return (
      <section className="card">
        <div className="card-head"><span className="card-title">Top 3 actions</span></div>
        <div className="empty">Ranking the period…</div>
      </section>
    );
  }

  if (top.length === 0) {
    return (
      <section className="card">
        <div className="card-head">
          <span className="card-title">Top 3 actions</span>
          <span className="scope-pill">{scope}</span>
        </div>
        <div className="empty">Nothing crosses a threshold for this period.</div>
      </section>
    );
  }

  return (
    <TableCard
      title="Top 3 actions"
      scope={scope}
      asof="Ranked by value at risk"
      cols={[
        { label: '#' },
        { label: 'Type', type: 'center' },
        { label: 'What to act on' },
        { label: 'Value at risk', type: 'num' },
      ]}
      rows={top.map((x, i) => ({
        key: `${x.kind}-${x.who}`,
        cells: [
          String(i + 1),
          <Tag key="t">{x.kind}</Tag>,
          <div key="w">
            <div className="finding-text">{x.text}</div>
            <div className="finding-meta">{x.math}</div>
          </div>,
          <span key="v" style={{ fontWeight: 600 }}>{fmt(x.w, 'money')}</span>,
        ],
      }))}
      foot={(
        <>
          <b>Recomputed for the selected period</b> — change the dates and these three change with
          them. Availability items are valued as unserved demand: the OSA gap to the{' '}
          {pOsa === null ? 'portfolio' : `${pOsa.toFixed(1)}%`} average applied to the units actually
          sold, at {fmt(vpu, 'money')} per unit — the period&rsquo;s own realised value on the{' '}
          {basis === 'mrp' ? 'MRP' : 'selling-price'} basis, not an assumption. Supply items are
          valued at undelivered units on purchase orders that have <b>closed</b>, so all three sit
          on one rupee scale. This deliberately does not use the sales-loss estimate, which runs
          about eight times the measured loss and would put every availability item on top for a
          reason that is an artefact of basis. The city items are measured against the unweighted
          portfolio OSA and the sub-line item against the sales-weighted one, because a row can only
          be compared with the definition it was computed on.
        </>
      )}
    />
  );
}
